/**
 * 附件持久化（T09）。
 *
 * 把 Attachment[] 经 `messageRepo` 的更新接口写回消息，并在 dataUrl 过大时给出明确提示
 * （本轮不做压缩，先保证行为可预期）。核心层契约见 `src/core/types.ts` 的 ChatMessage。
 */

import { messageRepo } from '../../core';
import type { Attachment, ChatMessage } from '../../core/types';
import { DATA_URL_WARN_BYTES, hasOversizedDataUrl } from './validate';

/** dataUrl 过大时展示给用户的提示文案。*/
export const DATA_URL_WARN_MESSAGE =
  '附件体积较大（dataUrl 超过 4MB），本地保存可能变慢甚至失败；本轮不做压缩，会按原样保存。';

export interface PersistAttachmentsResult {
  ok: boolean;
  /** 保存成功时的最新消息。*/
  message?: ChatMessage;
  /** 需要提示用户的警告（体积过大 / 消息不存在）。*/
  warning?: string;
}

export interface PersistAttachmentsOptions {
  /** 覆盖默认的体积提示阈值（测试用）。*/
  warnBytes?: number;
}

/** 把附件写回指定消息；返回结果与可选警告。*/
export async function persistAttachments(
  messageId: string,
  attachments: readonly Attachment[],
  options: PersistAttachmentsOptions = {},
): Promise<PersistAttachmentsResult> {
  const warnBytes = options.warnBytes ?? DATA_URL_WARN_BYTES;
  const message = await messageRepo.updateMessage(messageId, { attachments: [...attachments] });

  if (message === undefined) {
    return { ok: false, warning: '消息不存在，附件未能保存' };
  }

  if (hasOversizedDataUrl(attachments, warnBytes)) {
    console.warn(`[attachments] ${DATA_URL_WARN_MESSAGE}`);
    return { ok: true, message, warning: DATA_URL_WARN_MESSAGE };
  }

  return { ok: true, message };
}

/** 读回某条消息已保存的附件（没有则返回空数组）。*/
export async function readMessageAttachments(messageId: string): Promise<Attachment[]> {
  const message = await messageRepo.getMessage(messageId);
  return message?.attachments ?? [];
}
/** 新建一条消息并把附件写回；演示页与后续 T06 对话页都可复用。*/
export async function persistMessageWithAttachments(
  conversationId: string,
  attachments: readonly Attachment[],
  content = '（T09 附件演示）',
): Promise<PersistAttachmentsResult> {
  const message = await messageRepo.createAndSaveMessage({
    conversationId,
    role: 'user',
    content,
  });
  return persistAttachments(message.id, attachments);
}